import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Question, QuestionOption, ResponsePayload } from '../../../types';
import { DragAndDropRanking } from '../../questions/DragAndDropRanking';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const resolveOptionIds = (question: Question): number[] => {
  const options = Array.isArray(question?.options) ? question.options : [];
  return options
    .filter((option): option is QuestionOption => typeof option?.id === 'number')
    .map((option) => option.id);
};

/** A saved ranking, restricted to current options. Nothing is ranked by default. */
const resolveInitialOrder = (question: Question, existing: number[] | undefined): number[] => {
  if (!Array.isArray(existing) || existing.length === 0) {
    return [];
  }
  const optionIds = resolveOptionIds(question);
  return existing.filter((id, index) => optionIds.includes(id) && existing.indexOf(id) === index);
};

const DragAndDropRankingRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isSubmitting,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
}) => {
  const questionId = question?.id ?? existingResponse?.question ?? -1;

  const initialOrder = useMemo(
    () =>
      resolveInitialOrder(
        question,
        Array.isArray(existingResponse?.selected_options) ? existingResponse?.selected_options : undefined,
      ),
    [existingResponse, question],
  );

  const [orderedOptionIds, setOrderedOptionIds] = useState<number[]>(initialOrder);

  const optionIds = useMemo(() => resolveOptionIds(question), [question]);

  const emitDraft = useCallback(
    (selection: number[]) => {
      // Only a complete ranking is an answer; the server rejects partial rankings.
      const isComplete =
        optionIds.length > 0 && optionIds.every((id) => selection.includes(id));

      onHasUserInputChange?.(isComplete);
      onValidityChange?.(isComplete);

      if (!isComplete) {
        onDraftChange(null);
        return;
      }

      const payload: ResponsePayload = {
        question: questionId,
        selected_options: [...selection],
      };

      onDraftChange(payload);
    },
    [onDraftChange, onHasUserInputChange, onValidityChange, optionIds, questionId],
  );

  useEffect(() => {
    setOrderedOptionIds(initialOrder);
    emitDraft(initialOrder);
  }, [emitDraft, initialOrder, questionId]);

  const handleChange = (selection: number[]) => {
    setOrderedOptionIds(selection);
    emitDraft(selection);
  };

  return (
    <DragAndDropRanking
      question={question}
      selectedOptions={orderedOptionIds}
      onChange={handleChange}
      disabled={isSubmitting}
    />
  );
};

registerQuestionRenderer(['drag_and_drop_ranking'], DragAndDropRankingRenderer);
